import { Button } from '@strategy4kids/ui';
import { useMemo, useState } from 'react';
import { useT } from '../i18n/useT.js';
import { gateAnswer, makeGateQuestion } from '../lib/parent-gate.js';

/**
 * The grown-up gate for one thing: shows `children` only after the small arithmetic question is answered. Per visit: unlocking is not
 * remembered, so a child cannot walk back in. It is the same speed bump as the Settings gate (see `lib/parent-gate.ts`), reusing its words.
 */
export function ParentGateBox({ label, children }: { readonly label: string; readonly children: React.ReactNode }) {
  const { t } = useT('settings');
  const [open, setOpen] = useState(false);
  const [unlocked, setUnlocked] = useState(false);
  const [answer, setAnswer] = useState('');
  const [wrong, setWrong] = useState(false);
  const question = useMemo(() => makeGateQuestion(), []);

  if (unlocked) return <>{children}</>;
  if (!open) {
    return (
      <Button type="button" variant="ghost" className="mt-3" onClick={() => setOpen(true)} data-testid="pack-gate-open">
        {label}
      </Button>
    );
  }
  return (
    <form
      className="mt-3 flex flex-col gap-3"
      data-testid="pack-gate"
      onSubmit={(e) => {
        e.preventDefault();
        if (gateAnswer(question, answer)) {
          setUnlocked(true);
        } else {
          setWrong(true);
          setAnswer('');
        }
      }}
    >
      <p className="text-base font-bold text-ink" data-testid="pack-gate-question">
        {t('gate-question', { a: question.a, b: question.b })}
      </p>
      <input
        type="text"
        inputMode="numeric"
        autoComplete="off"
        value={answer}
        data-testid="pack-gate-answer"
        aria-label={t('gate-answer-label')}
        onChange={(e) => {
          setAnswer(e.target.value);
          setWrong(false);
        }}
        className="h-11 w-40 rounded-xl border border-border bg-surface px-4 text-base text-fg focus-visible:outline-none focus-visible:border-accent focus-visible:ring-2 focus-visible:ring-ring"
      />
      {wrong ? (
        <p className="text-sm font-bold text-red-600" role="alert" data-testid="pack-gate-error">
          {t('gate-wrong')}
        </p>
      ) : null}
      <Button type="submit" data-testid="pack-gate-submit">
        {t('gate-submit')}
      </Button>
    </form>
  );
}
